// contexts/AuthContext.js - Context para manejo global de autenticación
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  isAuthenticated, 
  clearAuth, 
  hasRole, 
  hasBusinessAreaAccess, 
  getUserRoles, 
  getUserBusinessAreas,
  setupProactiveRefresh,
  clearProactiveRefresh,
  getToken,
  getAssignedClientIds,
  } from '../utils/auth';
import authService from '../services/authService';
import menuService from '../services/menuService';
import userClientAssignmentService from '../services/userClientAssignmentService';
import { useCompanyConfig } from './CompanyConfigContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [userMenus, setUserMenus] = useState([]);
  const [menusLoading, setMenusLoading] = useState(false);
  const [assignedClients, setAssignedClients] = useState([]);
  const [assignedClientIds, setAssignedClientIds] = useState([]);
  
  // Usar ref para evitar múltiples inicializaciones
  const isInitialized = useRef(false);

  // Acceso al contexto de configuración de empresa
  const { setCompanyConfig, clearCompanyConfig } = useCompanyConfig();

  // Cargar menús del usuario
  const loadUserMenus = useCallback(async () => {

    setMenusLoading(true);
    try {
      const result = await menuService.getUserMenus();
      
      if (result.success) {

        const transformedMenus = menuService.transformMenusForFalcon(result.menus?.data);

        setUserMenus(transformedMenus);
      } else {

        const fallbackMenus = menuService.getFallbackMenus();
        setUserMenus(fallbackMenus);
      }
    } catch (error) {
      console.error('AuthContext: Error cargando menús:', error);
      
      if (process.env.NODE_ENV === 'development' && error.code === 'ECONNREFUSED') {

        const devMenus = menuService.getDefaultMenus();
        setUserMenus(devMenus);
      } else {

        const fallbackMenus = menuService.getFallbackMenus();
        setUserMenus(fallbackMenus);
      }
    } finally {
      setMenusLoading(false);
    }
  }, []);

  // Función de refresh proactivo automático
  const performProactiveRefresh = useCallback(async () => {
    try {

      const result = await authService.refreshToken();
      
      if (result.success) {

        // Reprogramar el siguiente refresh
        setupProactiveRefresh(performProactiveRefresh);
      } else {
        console.warn('⚠️ Refresh proactivo falló');
        // Si falla, dejar que el interceptor maneje los 401
      }
    } catch (error) {
      console.warn('⚠️ Error en refresh proactivo:', error.message);
      // Si falla, dejar que el interceptor maneje los 401
    }
  }, []);

  // Función de logout
  const logout = useCallback(async () => {
    try {
      clearProactiveRefresh(); // Limpiar timer de refresh
      await authService.logout();
    } catch (error) {
      console.error('Error durante logout:', error);
    } finally {
      setUser(null);
      setAuthenticated(false);
      setUserMenus([]);
      clearCompanyConfig();
      clearAuth();
    }
  }, [clearCompanyConfig]);

  // Cargar perfil del usuario
  const loadUserProfile = useCallback(async () => {
    try {
      const result = await authService.getProfile();
      if (result.success) {
        setUser(result.user);
        setAuthenticated(true);
        return true;
      } else {
        setUser(null);
        setAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Error cargando perfil:', error);
      setUser(null);
      setAuthenticated(false);
      return false;
    }
  }, []);

  // Cargar clientes asignados al usuario
  const loadAssignedClients = useCallback(async () => {
    try {
      const clients = await userClientAssignmentService.getMyClients();
      setAssignedClients(clients || []);
      setAssignedClientIds((clients || []).map(c => c.ClientID));
    } catch (error) {
      console.error('Error cargando clientes asignados:', error);
      // Si falla, intentar obtener del token
      const clientIdsFromToken = getAssignedClientIds();
      setAssignedClientIds(clientIdsFromToken || []);
    }
  }, []);

  // Cargar perfil, menús Y clientes asignados en secuencia
  const loadProfileAndMenus = useCallback(async () => {
    const profileLoaded = await loadUserProfile();
    if (profileLoaded) {
      await Promise.all([
        loadUserMenus(),
        loadAssignedClients()
      ]);
    }
  }, [loadUserProfile, loadUserMenus, loadAssignedClients]);

  // Escuchar eventos de expiración de token desde el interceptor de axios
  const isLoggingOut = useRef(false);
  useEffect(() => {
    const handleTokenExpired = () => {
      if (isLoggingOut.current) {
        return;
      }
      
      isLoggingOut.current = true;
      logout().finally(() => {
        // Redirigir con mensaje claro
        try {
          const params = new URLSearchParams({ reason: 'session_expired' });
          window.location.assign(`/login?${params.toString()}`);
        } catch(_) {}
        isLoggingOut.current = false;
      });
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, [logout]);

  // Función de login
  const login = useCallback(async (credentials) => {
    try {
      const result = await authService.login(credentials);
      
      if (result.success) {
        if (result.companyConfig) {
          setCompanyConfig(result.companyConfig);
        }
        
        await loadProfileAndMenus();
        
        // Configurar refresh proactivo después del login exitoso
        const token = getToken();
        if (token) {
        setupProactiveRefresh(performProactiveRefresh);
        }
        
        // Retornar el usuario para que LoginForm pueda usarlo en la redirección
        return { success: true, user: result.user };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }, [setCompanyConfig, loadProfileAndMenus, performProactiveRefresh]);

  // Función de registro
  const register = useCallback(async (userData) => {
    try {
      const result = await authService.register(userData);
      
      if (result.success && result.user && !result.autoLoginFailed) {
        await loadProfileAndMenus();
      }
      
      return result;
    } catch (error) {
      console.error('Error en registro:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }, [loadProfileAndMenus]);

  // Inicializar estado de autenticación al cargar la app
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }
    
    isInitialized.current = true;
    
    const initAuth = async () => {
      try {
        const loadedFromCookies = authService.loadFromCookies();
        const hasTokens = isAuthenticated();
        
        if (loadedFromCookies || hasTokens) {
          // Si hay tokens, cargar perfil y menús
          setAuthenticated(true);
          await loadProfileAndMenus();
          
          // Configurar refresh proactivo si ya hay tokens
          const token = getToken();
          if (token) {
            setupProactiveRefresh(performProactiveRefresh);
          }
        } else {
          setAuthenticated(false);
          setUser(null);
          setUserMenus([]);
          clearAuth();
        }
      } catch (error) {
        console.error('Error inicializando autenticación:', error);
        setAuthenticated(false);
        setUser(null);
        setUserMenus([]);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [loadProfileAndMenus, performProactiveRefresh]);

  // Actualizar perfil y menús
  const updateProfile = useCallback(async () => {
    try {
      await loadProfileAndMenus();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error al actualizar perfil' };
    }
  }, [loadProfileAndMenus]);

  // Actualizar usuario directamente
  const updateUser = useCallback((updatedUser) => {
    if (updatedUser && typeof updatedUser === 'object') {
      setUser(updatedUser);
    }
  }, []);

  // Verificar si el usuario tiene un rol específico
  const checkHasRole = (role) => hasRole(role);

  // Verificar si el usuario tiene acceso a un área de negocio
  const checkHasBusinessAreaAccess = (businessAreaCode) => hasBusinessAreaAccess(businessAreaCode);

  // Verificar si es administrador
  const isAdmin = () => checkHasRole('admin');

  // Recargar menús manualmente desde el backend
  const refreshMenus = useCallback(() => {
    if (authenticated) {
      loadUserMenus();
    }
  }, [authenticated, loadUserMenus]);

  // Verificar si el usuario tiene acceso a un cliente específico
  const hasClientAccess = useCallback((clientId) => {
    // Super admin tiene acceso a todos
    if (user?.isSuperAdmin) return true;
    // Si no hay clientes asignados, tiene acceso a todos (comportamiento legacy)
    if (!assignedClientIds || assignedClientIds.length === 0) return true;
    // Verificar si el cliente está en la lista de asignados
    return assignedClientIds.includes(Number(clientId));
  }, [user, assignedClientIds]);

  // Filtrar una lista de items por clientes asignados
  const filterByAssignedClients = useCallback((items, clientIdField = 'clientId') => {
    // Super admin ve todos
    if (user?.isSuperAdmin) return items;
    // Si no hay clientes asignados, ver todos (comportamiento legacy)
    if (!assignedClientIds || assignedClientIds.length === 0) return items;
    // Filtrar por clientes asignados
    return items.filter(item => assignedClientIds.includes(Number(item[clientIdField])));
  }, [user, assignedClientIds]);

  // Refrescar clientes asignados
  const refreshAssignedClients = useCallback(async () => {
    if (authenticated) {
      await loadAssignedClients();
    }
  }, [authenticated, loadAssignedClients]);

  const value = {
    user,
    loading,
    authenticated,
    userMenus,
    menusLoading,
    login,
    register,
    logout,
    updateProfile,
    updateUser,
    refreshMenus,
    hasRole: checkHasRole,
    hasBusinessAreaAccess: checkHasBusinessAreaAccess,
    isAdmin,
    getUserRoles,
    getUserBusinessAreas,
    // Nuevas funciones para clientes asignados
    assignedClients,
    assignedClientIds,
    hasClientAccess,
    filterByAssignedClients,
    refreshAssignedClients
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 